import React, { useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { SITE, NAV_LINKS } from '@/lib/site';

export default function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  React.useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 bg-[#0A0A0A] border-b border-[#D4AF37]/30"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex items-center justify-between gap-6 h-20">
        <Link
          to="/"
          className="flex items-center gap-3 flex-shrink-0"
          data-testid="header-logo-link"
        >
          <img
            src={SITE.logoUrl}
            alt="Ball Raw Performance"
            className="h-12 w-12 object-contain"
          />
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="font-display text-white text-lg tracking-wider uppercase">
              Ball Raw
            </span>
            <span className="text-[#D4AF37] text-[10px] uppercase tracking-[0.3em] font-bold">
              Performance
            </span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.path}
              to={link.path}
              end={link.path === '/'}
              data-testid={`nav-link-${link.path.replace(/\//g, '') || 'home'}`}
              className={({ isActive }) =>
                `relative font-display uppercase tracking-[0.18em] text-[12px] font-semibold transition-colors duration-200 ${
                  isActive
                    ? 'text-[#D4AF37]'
                    : 'text-white hover:text-[#D4AF37]'
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-4">
          <a
            href={`tel:${SITE.phone.replace(/[^\d]/g, '')}`}
            className="hidden xl:flex items-center gap-2 text-white/80 hover:text-[#D4AF37] transition text-sm whitespace-nowrap"
            data-testid="header-phone-link"
          >
            <Phone size={14} strokeWidth={2} />
            <span className="tracking-wide">{SITE.phone}</span>
          </a>
          <Link
            to="/partnerships#contact"
            className="btn-brp-primary"
            data-testid="header-cta-discovery-call"
          >
            Discovery Call
          </Link>
        </div>

        <button
          aria-label="Toggle menu"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden text-white p-2"
          data-testid="header-mobile-toggle"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div
          className="lg:hidden bg-[#0A0A0A] border-t border-[#D4AF37]/20"
          data-testid="mobile-nav-panel"
        >
          <nav className="px-6 py-6 flex flex-col gap-4">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.path}
                to={link.path}
                end={link.path === '/'}
                data-testid={`mobile-nav-link-${link.path.replace(/\//g, '') || 'home'}`}
                className={({ isActive }) =>
                  `font-display uppercase tracking-[0.18em] text-sm py-2 ${
                    isActive ? 'text-[#D4AF37]' : 'text-white'
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
            <Link
              to="/partnerships#contact"
              className="btn-brp-primary mt-2"
              data-testid="mobile-cta-discovery-call"
            >
              Schedule Discovery Call
            </Link>
            <a
              href={`tel:${SITE.phone.replace(/[^\d]/g, '')}`}
              className="text-white/80 text-sm pt-2 flex items-center gap-2"
              data-testid="mobile-phone-link"
            >
              <Phone size={14} /> {SITE.phone}
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
